import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core'
import { FilterGroup } from '@helios/common-api'
import { cacheLoadingFilterTooltip } from '@helios/generic-components'
import { FilterGroupUpdate } from '../../../services/filter-group.service'
import {
  DescriptionModalTitles,
  UpdateDescriptionModalService,
} from '../../modals/update-description-modal/update-description-modal.service'

export interface FilterGroupCardAction {
  displayName: string
  tooltip: (cacheLoaded: boolean, filterGroup: FilterGroup) => string
  disabled: (cacheLoaded: boolean) => boolean
  onClick: (group: FilterGroup) => void
}

const ALLOWED_CHARACTERS_PATTERN = /^[a-zA-Z0-9\s]*$/

@Component({
  selector: 'hs-filter-group-card',
  templateUrl: './filter-group-card.component.html',
  styleUrls: ['./filter-group-card.component.scss'],
})
export class FilterGroupCardComponent implements AfterViewInit {
  _filterGroup?: FilterGroup
  @Input() set filterGroup(group: FilterGroup | undefined) {
    this._filterGroup = group
    this.name = group?.name
    this.description = group?.description
  }

  @Input() cacheLoaded: boolean | null = false

  @Output() triggerUpdateFilters = new EventEmitter<FilterGroup>()
  @Output() updateFilterGroup = new EventEmitter<{
    filterGroup: FilterGroup
    update: FilterGroupUpdate
  }>()
  @Output() deleteFilterGroup = new EventEmitter<FilterGroup>()
  @Output() cloneFilterGroup = new EventEmitter<FilterGroup>()
  @Output() copyFilterGroupPrefix = new EventEmitter<string>()
  @Output() applyFilterGroup = new EventEmitter<FilterGroup>()

  name?: string
  description?: string
  descriptionOnTooltip?: string

  filterGroupActions: FilterGroupCardAction[] = [
    {
      displayName: 'Apply',
      tooltip: (cacheLoaded: boolean, filterGroup: FilterGroup) => {
        if (!cacheLoaded) {
          return cacheLoadingFilterTooltip
        }
        return `Apply Filter Group - ${filterGroup.name}.`
      },
      disabled: (cacheLoaded: boolean) => !cacheLoaded,
      onClick: (group: FilterGroup) => this.apply(group),
    },
    {
      displayName: 'Clone',
      tooltip: (cacheLoaded: boolean, filterGroup: FilterGroup) => {
        return `Clone Filter Group - ${filterGroup.name}.`
      },
      disabled: () => false,
      onClick: (group: FilterGroup) => this.clone(group),
    },
    {
      displayName: 'Copy Filter Group ID',
      tooltip: () => {
        return `Copies the Filter Group's ID. The ID allows you to create a Filter Group from this one. This ID can be shared with others.`
      },
      disabled: () => false,
      onClick: (group: FilterGroup) => this.copyPrefix(group),
    },
    {
      displayName: 'Delete',
      tooltip: (cacheLoaded: boolean, filterGroup: FilterGroup) => {
        return `Delete Filter Group - ${filterGroup.name}.`
      },
      disabled: () => false,
      onClick: (group: FilterGroup) => this.delete(group),
    },
    {
      displayName: 'Update',
      tooltip: (cacheLoaded: boolean, filterGroup: FilterGroup) => {
        if (!cacheLoaded) {
          return cacheLoadingFilterTooltip
        }
        return `Update Filter Group - ${filterGroup.name}.`
      },
      disabled: (cacheLoaded: boolean) => !cacheLoaded,
      onClick: (group: FilterGroup) => this.updateFilters(group),
    },
  ].sort((action1, action2) =>
    action1.displayName < action2.displayName ? -1 : 1,
  )

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly updateDescriptionModalService: UpdateDescriptionModalService,
  ) {}

  ngAfterViewInit() {
    /**
     * Inline edit component throws ExpressionChangedAfterItHasBeenCheckedError without this.
     */
    this.cd.detectChanges()
  }

  updateName(group: FilterGroup) {
    if (!this.name || !ALLOWED_CHARACTERS_PATTERN.test(this.name)) {
      this.name = group.name
      return
    }
    this.emitUpdate(group, { name: this.name })
  }

  updateDescription(group: FilterGroup) {
    if (
      this.description &&
      !ALLOWED_CHARACTERS_PATTERN.test(this.description)
    ) {
      this.description = group.description
      return
    }
    this.emitUpdate(group, { description: this.description })
  }

  updateFilters(group: FilterGroup) {
    this.triggerUpdateFilters.emit(group)
  }

  apply(group: FilterGroup) {
    this.applyFilterGroup.emit(group)
  }

  clone(group: FilterGroup) {
    this.cloneFilterGroup.emit(group)
  }

  copyPrefix(group: FilterGroup) {
    this.copyFilterGroupPrefix.emit(group.filterGroupPrefix)
  }

  delete(group: FilterGroup) {
    this.deleteFilterGroup.emit(group)
  }

  private emitUpdate(group: FilterGroup, update: FilterGroupUpdate) {
    this.filterGroup = {
      ...group,
      ...update,
    }
    this.updateFilterGroup.emit({
      filterGroup: group,
      update,
    })
  }

  async onDescriptionClick() {
    if (this._filterGroup == null) return
    const group = this._filterGroup
    await this.updateDescriptionModalService
      .toggle(this.description ?? '', DescriptionModalTitles.FilterGroup)
      .then(newDescription => {
        this.description = newDescription
        this.updateDescription(group)
      })
      .catch(() => {
        //Do Nothing
      })
  }

  onDescriptionHover() {
    this.descriptionOnTooltip = !this.description
      ? 'No Description'
      : this.description
  }
}
